/**
 * Simplified Property Form Component
 * 
 * Shows global property definition as read-only and only allows
 * editing of prime-specific override fields:
 * - is_required (override global default)
 * - default_value (override global default)
 * - property_scope (artifact vs artifact_item)
 * - state_mappings (which states create/update this property)
 * - referenced_artifacts (for reference properties)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Space,
  Typography,
  Divider,
  Alert,
  Descriptions,
  Spin,
  Checkbox,
  Button,
  message
} from 'antd';
import {
  InfoCircleOutlined,
  LinkOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { GlobalPropertiesService } from '../../services/api/globalPropertiesService';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { PrimeArtifactState } from '../../types/primeModel';
import { apiClient } from '../../services/apiClient';

const { Option } = Select;
const { Text, Title } = Typography;

interface GlobalPropertyDefinition {
  property_key: string;
  property_type?: string;
  description?: string;
  input_source?: string;
  validation_rules?: Record<string, any>;
  default_value?: any;
  global_option_catalog_ref?: string;
  state_actions?: any[];
}

interface PropertyStateMapping {
  state_name: string;
  mapping_type: 'created' | 'updated';
}

interface PropertyArtifactReference {
  referenced_artifact_type_id?: string;
  artifact_id?: string;
  reference_type?: 'uuid' | 'transformed';
  source_property_key?: string;
  transformation_logic?: any;
  relationship_type?: string;
  notes?: string;
}

interface SimplifiedPropertyFormProps {
  visible: boolean;
  propertyKey: string;
  artifactId: string;
  editingPropertyId?: string; // If editing existing property
  existingOverrides?: {
    is_required?: boolean;
    default_value?: any;
    property_scope?: 'artifact' | 'artifact_item';
    state_mappings?: PropertyStateMapping[];
    referenced_artifacts?: PropertyArtifactReference[];
    is_child_reference?: boolean;
  };
  onCancel: () => void;
  onSave: () => void;
}

export const SimplifiedPropertyForm: React.FC<SimplifiedPropertyFormProps> = ({
  visible,
  propertyKey,
  artifactId,
  editingPropertyId,
  existingOverrides,
  onCancel,
  onSave
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [globalProperty, setGlobalProperty] = useState<GlobalPropertyDefinition | null>(null);
  const [globalPropertyError, setGlobalPropertyError] = useState<string | null>(null);
  const [artifactStates, setArtifactStates] = useState<PrimeArtifactState[]>([]);

  // Fetch global property definition
  const fetchGlobalProperty = useCallback(async () => {
    if (!propertyKey) return;
    
    setLoading(true);
    setGlobalPropertyError(null);
    
    try {
      const response = await GlobalPropertiesService.getPropertyDefinition(propertyKey);
      setGlobalProperty({
        property_key: response.property_key,
        property_type: response.definition?.property_type,
        description: response.definition?.description,
        input_source: response.definition?.input_source,
        validation_rules: response.definition?.validation_rules,
        default_value: response.definition?.default_value,
        global_option_catalog_ref: response.definition?.global_option_catalog_ref,
        state_actions: response.definition?.state_actions
      });
    } catch (error: any) {
      console.error('Error fetching global property:', error);
      setGlobalPropertyError(`Could not load global property definition: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [propertyKey]);

  // Fetch artifact states
  const fetchArtifactStates = useCallback(async () => {
    try {
      const states = await PrimeArtifactService.getPrimeArtifactStates(artifactId);
      setArtifactStates(states || []);
    } catch (error) {
      console.error('Error fetching artifact states:', error);
    }
  }, [artifactId]);

  // Load data when modal opens
  useEffect(() => {
    if (visible) {
      void fetchGlobalProperty();
      void fetchArtifactStates();
      
      // Set form initial values from existing overrides
      if (existingOverrides) {
        const createdStates = existingOverrides.state_mappings
          ?.filter(m => m.mapping_type === 'created')
          .map(m => m.state_name) || [];
        const updatedStates = existingOverrides.state_mappings
          ?.filter(m => m.mapping_type === 'updated')
          .map(m => m.state_name) || [];
        
        form.setFieldsValue({
          is_required: existingOverrides.is_required || false,
          default_value: existingOverrides.default_value,
          property_scope: existingOverrides.property_scope,
          created_in_states: createdStates,
          updated_in_states: updatedStates,
          is_child_reference: existingOverrides.is_child_reference
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, fetchGlobalProperty, fetchArtifactStates, existingOverrides, form]);

  // Handle save
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Build state mappings
      const stateMappings: PropertyStateMapping[] = [];
      if (values.created_in_states?.length > 0) {
        values.created_in_states.forEach((stateName: string) => {
          stateMappings.push({ state_name: stateName, mapping_type: 'created' });
        });
      }
      if (values.updated_in_states?.length > 0) {
        values.updated_in_states.forEach((stateName: string) => {
          stateMappings.push({ state_name: stateName, mapping_type: 'updated' });
        });
      }

      // Build property data - inherit from global, apply overrides
      const propertyData = {
        property_key: propertyKey,
        // Inherit from global property
        property_type: globalProperty?.property_type || 'string',
        description: globalProperty?.description,
        input_source: globalProperty?.input_source || 'user_input',
        validation_rules: globalProperty?.validation_rules,
        global_option_catalog_ref: globalProperty?.global_option_catalog_ref,
        // Apply overrides
        is_required: values.is_required || false,
        default_value: values.default_value || globalProperty?.default_value,
        property_scope: values.property_scope,
        is_child_reference: values.is_child_reference,
        state_mappings: stateMappings,
        // Self-reference for properties that don't reference other artifacts
        referenced_artifacts: existingOverrides?.referenced_artifacts?.length 
          ? existingOverrides.referenced_artifacts 
          : [{
              artifact_id: artifactId,
              reference_type: 'self',
              referenced_artifact_type_id: null
            }]
      };

      if (editingPropertyId) {
        await apiClient.put(`/prime-artifacts/${artifactId}/properties/${editingPropertyId}`, propertyData);
        message.success('Property updated successfully');
      } else {
        await apiClient.post(`/prime-artifacts/${artifactId}/properties`, propertyData);
        message.success('Property added successfully');
      }

      onSave();
    } catch (error: any) {
      console.error('Error saving property:', error);
      if (error.errorFields?.length > 0) {
        message.error('Please fix validation errors');
      } else {
        message.error(`Failed to save property: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // Get property type color
  const getPropertyTypeColor = (type?: string) => {
    const colorMap: Record<string, string> = {
      string: 'blue',
      number: 'green',
      integer: 'green',
      float: 'green',
      boolean: 'orange',
      date: 'purple',
      date_range: 'purple',
      currency: 'cyan',
      duration: 'geekblue',
      url: 'gold',
      uuid: 'magenta',
      'uuid[]': 'red',
      list: 'lime'
    };
    return colorMap[type || ''] || 'default';
  };

  // Get input source color
  const getInputSourceColor = (source?: string) => {
    const colorMap: Record<string, string> = {
      user_input: 'blue',
      system_generated: 'green',
      referenced_artifact: 'purple',
      transformed_reference: 'orange'
    };
    return colorMap[source || ''] || 'default';
  };

  // Get input source label
  const getInputSourceLabel = (source?: string) => {
    const labelMap: Record<string, string> = {
      user_input: 'User Input',
      system_generated: 'System Generated',
      referenced_artifact: 'Referenced Artifact',
      transformed_reference: 'Transformed Reference'
    };
    return labelMap[source || ''] || source || 'Unknown';
  };

  return (
    <Modal
      title={
        <Space>
          {editingPropertyId ? 'Edit Property' : 'Add Property'}
          <Tag color="blue">{propertyKey}</Tag>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={saving}
      width={700}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin tip="Loading global property definition..." />
        </div>
      ) : globalPropertyError ? (
        <Alert
          message="Error Loading Global Property"
          description={globalPropertyError}
          type="error"
          showIcon
        />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Global Property Info - Read Only */}
          <div>
            <Title level={5}>
              <GlobalOutlined style={{ marginRight: 8 }} />
              Global Property Definition (Read-Only)
            </Title>
            <Alert
              message="This property inherits its definition from a global property"
              description="The fields below are inherited from the global property definition and cannot be modified here. Edit the global property to change these values."
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 16 }}
            />
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Property Type">
                <Tag color={getPropertyTypeColor(globalProperty?.property_type)}>
                  {globalProperty?.property_type || 'string'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Input Source">
                <Tag color={getInputSourceColor(globalProperty?.input_source)}>
                  {getInputSourceLabel(globalProperty?.input_source)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {globalProperty?.description || <Text type="secondary">No description</Text>}
              </Descriptions.Item>
              {globalProperty?.validation_rules && Object.keys(globalProperty.validation_rules).length > 0 && (
                <Descriptions.Item label="Validation Rules" span={2}>
                  <Text code>{JSON.stringify(globalProperty.validation_rules)}</Text>
                </Descriptions.Item>
              )}
              {globalProperty?.default_value && (
                <Descriptions.Item label="Global Default">
                  <Text code>{String(globalProperty.default_value)}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>

          <Divider />

          {/* Prime-Specific Overrides - Editable */}
          <div>
            <Title level={5}>
              <LinkOutlined style={{ marginRight: 8 }} />
              Prime Artifact Overrides
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Configure how this property behaves for this specific prime artifact
            </Text>
            
            <Form form={form} layout="vertical">
              <Form.Item
                label="Required"
                name="is_required"
                valuePropName="checked"
                tooltip="Override the global default - make this property required for this artifact"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label="Default Value Override"
                name="default_value"
                tooltip="Override the global default value for this specific artifact"
              >
                <Input 
                  placeholder={globalProperty?.default_value 
                    ? `Global default: ${globalProperty.default_value}` 
                    : 'Enter default value for this artifact'}
                />
              </Form.Item>

              <Form.Item
                label="Property Scope"
                name="property_scope"
                tooltip="Artifact scope: applies to the artifact itself. ArtifactItem scope: applies to individual items."
              >
                <Select placeholder="Select scope (or leave blank to auto-detect)">
                  <Option value="artifact">Artifact</Option>
                  <Option value="artifact_item">Artifact Item</Option>
                </Select>
              </Form.Item>

              {(globalProperty?.input_source === 'referenced_artifact' || 
                globalProperty?.input_source === 'transformed_reference') && (
                <Form.Item
                  label="Child Reference"
                  name="is_child_reference"
                  valuePropName="checked"
                  tooltip="Child references require creation and the workflow waits for the child artifact"
                >
                  <Checkbox>This is a child reference (requires creation step)</Checkbox>
                </Form.Item>
              )}

              <Divider />
              <Title level={5}>State Mappings</Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Configure which states create or update this property
              </Text>

              <Form.Item
                label="Created In States"
                name="created_in_states"
                tooltip="States where this property is first initialized"
              >
                <Select
                  mode="multiple"
                  placeholder="Select states where property is created"
                  allowClear
                >
                  {artifactStates.map(state => (
                    <Option key={state.id} value={state.name}>
                      {state.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Updated In States"
                name="updated_in_states"
                tooltip="States where this property value is updated"
              >
                <Select
                  mode="multiple"
                  placeholder="Select states where property is updated"
                  allowClear
                >
                  {artifactStates.map(state => (
                    <Option key={state.id} value={state.name}>
                      {state.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </div>

          {/* State Actions from Global (if available) */}
          {globalProperty?.state_actions && globalProperty.state_actions.length > 0 && (
            <>
              <Divider />
              <div>
                <Title level={5}>
                  <InfoCircleOutlined style={{ marginRight: 8 }} />
                  Global State Actions (Reference)
                </Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  States suggested by the global property definition
                </Text>
                <Space wrap>
                  {globalProperty.state_actions.map((action, index) => (
                    <Tag key={index} color="geekblue">
                      {action.state || action.state_name || JSON.stringify(action)}
                    </Tag>
                  ))}
                </Space>
              </div>
            </>
          )}
        </Space>
      )}
    </Modal>
  );
};

export default SimplifiedPropertyForm;
